/**
 * Obstacle system — spikes, walls, moving, rotating obstacles + collision.
 *
 * Supports static and dynamic obstacles. Dynamic obstacles update their
 * position each frame based on their behavior definition.
 */

import type { Color, Rect } from '@/types';
import type { ObstacleBehavior } from './level';

export type ObstacleType = 'spike' | 'wall' | 'moving' | 'rotating' | 'wave' | 'jumppad' | 'orb';

export type PadSize = 'small' | 'medium' | 'large';
export type OrbSize = 'small' | 'medium' | 'large';

export interface Obstacle {
  type: ObstacleType;
  x: number;
  y: number;
  width: number;
  height: number;
  color: Color;
  /** Tilt angle in radians — aligns the obstacle with the ground tangent */
  angle: number;
  // Dynamic obstacle support
  baseX: number;
  baseY: number;
  behavior: ObstacleBehavior | null;
  time: number;
  // Jump pad support (only set on type='jumppad')
  padSize?: PadSize;
  padCooldown?: number;
  firedTimer?: number;
  // Orb support (only set on type='orb')
  orbSize?: OrbSize;
}

/** Default footprint per tier — width × height of the dome's bounding box */
export const JUMPPAD_DEFAULT_SIZES: Record<PadSize, { width: number; height: number }> = {
  small:  { width: 24, height:  8 },
  medium: { width: 32, height: 12 },
  large:  { width: 40, height: 16 },
};

/** Tier color (RGB normalized, alpha 1) */
export const JUMPPAD_TIER_COLORS: Record<PadSize, Color> = {
  small:  { r: 1.0, g: 0.2, b: 0.333, a: 1 },  // #ff3355
  medium: { r: 0.2, g: 1.0, b: 0.4,   a: 1 },  // #33ff66
  large:  { r: 0.2, g: 0.4, b: 1.0,   a: 1 },  // #3366ff
};

/** Create a jump pad on the ground at world position x, baseY */
export function createJumpPad(x: number, y: number, padSize: PadSize): Obstacle {
  const { width, height } = JUMPPAD_DEFAULT_SIZES[padSize];
  return {
    type: 'jumppad',
    x, y,
    width, height,
    color: { ...JUMPPAD_TIER_COLORS[padSize] },
    angle: 0,
    baseX: x, baseY: y,
    behavior: null, time: 0,
    padSize,
    padCooldown: 0,
    firedTimer: 0,
  };
}

/** Default orb diameter per tier (orbs are circles; width = height = diameter) */
export const ORB_DEFAULT_DIAMETERS: Record<OrbSize, number> = {
  small:  20,
  medium: 28,
  large:  36,
};

/** Tier color (same RGB primaries as pads) */
export const ORB_TIER_COLORS: Record<OrbSize, Color> = {
  small:  { r: 1.0, g: 0.2, b: 0.333, a: 1 },  // #ff3355
  medium: { r: 0.2, g: 1.0, b: 0.4,   a: 1 },  // #33ff66
  large:  { r: 0.2, g: 0.4, b: 1.0,   a: 1 },  // #3366ff
};

/** Create a mid-air orb at world position (x, y). 'y' is ground-relative (the level loader applies groundY). */
export function createOrb(x: number, y: number, orbSize: OrbSize): Obstacle {
  const diameter = ORB_DEFAULT_DIAMETERS[orbSize];
  return {
    type: 'orb',
    x, y,
    width: diameter, height: diameter,
    color: { ...ORB_TIER_COLORS[orbSize] },
    angle: 0,
    baseX: x, baseY: y,
    behavior: null, time: 0,
    orbSize,
    firedTimer: 0,
  };
}

/** Create a spike obstacle at a position on the ground */
export function createSpike(x: number, groundY: number, color: Color): Obstacle {
  return {
    type: 'spike',
    x, y: groundY,
    width: 24, height: 36,
    color, angle: 0,
    baseX: x, baseY: groundY,
    behavior: null, time: 0,
  };
}

/** Create a wall obstacle */
export function createWall(
  x: number, y: number, width: number, height: number, color: Color,
): Obstacle {
  return {
    type: 'wall',
    x, y, width, height, color, angle: 0,
    baseX: x, baseY: y,
    behavior: null, time: 0,
  };
}

/** Create a moving obstacle */
export function createMoving(
  x: number, y: number, width: number, height: number,
  color: Color, behavior: ObstacleBehavior,
): Obstacle {
  return {
    type: 'moving',
    x, y, width, height, color, angle: 0,
    baseX: x, baseY: y,
    behavior, time: 0,
  };
}

/** Update dynamic obstacles (moving, rotating, wave) and decay jumppad timers */
export function updateObstacles(obstacles: Obstacle[], dt: number): void {
  for (const obs of obstacles) {
    // Jump pad timer decay
    if (obs.type === 'jumppad') {
      if ((obs.padCooldown ?? 0) > 0) {
        obs.padCooldown = Math.max(0, (obs.padCooldown ?? 0) - dt);
      }
      if ((obs.firedTimer ?? 0) > 0) {
        obs.firedTimer = Math.max(0, (obs.firedTimer ?? 0) - dt);
      }
      continue;
    }

    // Orb timer decay (no cooldown — orbs gate on jump-press)
    if (obs.type === 'orb') {
      if ((obs.firedTimer ?? 0) > 0) {
        obs.firedTimer = Math.max(0, (obs.firedTimer ?? 0) - dt);
      }
      continue;
    }

    if (!obs.behavior) continue;
    obs.time += dt;

    const { motion, amplitude, frequency, phase } = obs.behavior;
    const t = obs.time;

    switch (motion) {
      case 'sine':
        obs.y = obs.baseY + amplitude * Math.sin(frequency * t * Math.PI * 2 + phase);
        break;
      case 'cosine':
        obs.x = obs.baseX + amplitude * Math.cos(frequency * t * Math.PI * 2 + phase);
        break;
      case 'circular': {
        obs.x = obs.baseX + amplitude * Math.cos(frequency * t * Math.PI * 2 + phase);
        obs.y = obs.baseY + amplitude * Math.sin(frequency * t * Math.PI * 2 + phase);
        break;
      }
      default:
        break;
    }
  }
}

/** Collision test — handles tilted obstacles by transforming into local space */
export function checkCollision(playerBounds: Rect, obstacle: Obstacle): boolean {
  const shrink = obstacle.type === 'spike' ? 6 : 2;

  // Player as a circle (approx)
  const pcx = playerBounds.x + playerBounds.width / 2;
  const pcy = playerBounds.y + playerBounds.height / 2;
  const pr = Math.min(playerBounds.width, playerBounds.height) / 2;

  // Obstacle pivot = center of its base (on the ground)
  const pivotX = obstacle.x + obstacle.width / 2;
  const pivotY = obstacle.y;

  // Transform player center into obstacle local space (un-rotate around pivot)
  const dx = pcx - pivotX;
  const dy = pcy - pivotY;
  const cos = Math.cos(-obstacle.angle);
  const sin = Math.sin(-obstacle.angle);
  const lx = dx * cos - dy * sin;
  const ly = dx * sin + dy * cos;

  // Shrunken rect in local coords: x ∈ [-w/2 + shrink, w/2 - shrink], y ∈ [shrink, h - shrink]
  const minX = -obstacle.width / 2 + shrink;
  const maxX = obstacle.width / 2 - shrink;
  const minY = shrink;
  const maxY = obstacle.height - shrink;

  // Closest point on rect to player center (clamp)
  const nx = Math.max(minX, Math.min(maxX, lx));
  const ny = Math.max(minY, Math.min(maxY, ly));

  // Distance from player center to closest rect point
  const ddx = lx - nx;
  const ddy = ly - ny;
  return ddx * ddx + ddy * ddy < pr * pr;
}

/**
 * Detect a player-pad contact. Returns null if no contact (or pad on cooldown).
 * `topLand` is true when the player is descending (or stationary) AND their
 * feet were at or above the pad's top surface in the previous frame.
 */
export function checkJumpPadHit(
  playerBounds: Rect,
  prevFeetY: number,
  velocityY: number,
  pad: Obstacle,
): { topLand: boolean } | null {
  if (pad.type !== 'jumppad') return null;
  if ((pad.padCooldown ?? 0) > 0) return null;

  // AABB overlap test (pads aren't tilted enough to need rotated collision)
  const padLeft = pad.x;
  const padRight = pad.x + pad.width;
  const padBottom = pad.y;
  const padTop = pad.y + pad.height;

  if (
    playerBounds.x + playerBounds.width  < padLeft  ||
    playerBounds.x                       > padRight ||
    playerBounds.y + playerBounds.height < padBottom ||
    playerBounds.y                       > padTop
  ) {
    return null;
  }

  const topLand = velocityY <= 0 && prevFeetY >= padTop;
  return { topLand };
}

/**
 * Circle-circle overlap test for orbs. Returns true if the player (treated as
 * a circle inscribed in their bounding box) overlaps the orb (a circle
 * inscribed in its bounding box). Returns false for non-orb obstacles.
 */
export function checkOrbOverlap(playerBounds: Rect, orb: Obstacle): boolean {
  if (orb.type !== 'orb') return false;
  const orbR = orb.width / 2;
  const orbCx = orb.x + orbR;
  const orbCy = orb.y + orbR;
  const playerR = Math.min(playerBounds.width, playerBounds.height) / 2;
  const playerCx = playerBounds.x + playerBounds.width / 2;
  const playerCy = playerBounds.y + playerBounds.height / 2;
  const dx = playerCx - orbCx;
  const dy = playerCy - orbCy;
  const reach = orbR + playerR;
  return dx * dx + dy * dy < reach * reach;
}

/** Check graze (near-miss) — returns distance if close, null if not */
export function checkGraze(playerBounds: Rect, obstacle: Obstacle, threshold = 20): number | null {
  // Expand the obstacle bounds by the threshold
  const ox = obstacle.x - threshold;
  const oy = obstacle.y - threshold;
  const ow = obstacle.width + threshold * 2;
  const oh = obstacle.height + threshold * 2;

  // Check if player is within graze range but NOT colliding
  const inRange = (
    playerBounds.x < ox + ow &&
    playerBounds.x + playerBounds.width > ox &&
    playerBounds.y < oy + oh &&
    playerBounds.y + playerBounds.height > oy
  );

  if (!inRange) return null;
  if (checkCollision(playerBounds, obstacle)) return null;

  // Compute approximate distance to obstacle center
  const pcx = playerBounds.x + playerBounds.width / 2;
  const pcy = playerBounds.y + playerBounds.height / 2;
  const ocx = obstacle.x + obstacle.width / 2;
  const ocy = obstacle.y + obstacle.height / 2;
  const dx = pcx - ocx;
  const dy = pcy - ocy;
  return Math.sqrt(dx * dx + dy * dy);
}
